import { getBackendUrl } from '../utils/utils';
import { appendCsvLanguage, csvFileName } from '../utils/csv';

export type BadgeKey =
    | 'first_session'
    | 'streak_5'
    | 'streak_14'
    | 'streak_30'
    | 'streak_50'
    | 'streak_100'
    | 'streak_365'
    | 'level_up_practitioner'
    | 'perfect_score'
    | 'scenario_explorer';

export type LeaderboardPeriod = 'week' | 'month' | 'all_time';

// ─── /gamification/me ────────────────────────────────────────────────────────

export interface BadgeResponse {
    key: BadgeKey;
    name: string;
    description: string;
    awarded_at: string;
    awarded_by: string | null;
    seen: boolean;
}

export interface RecentXpEntry {
    amount: number;
    reason: string;
    multiplier: number;
    session_id: string | null;
    created_at: string;
}

export interface DifficultyStats {
    completed: number;
    total: number;
}

export interface SessionsByDifficulty {
    easy: DifficultyStats;
    medium: DifficultyStats;
    hard: DifficultyStats;
    standard: DifficultyStats;
}

export interface GamificationMeResponse {
    xp_total: number;
    level: number;
    level_name: string;
    xp_to_next_level: number | null;
    streak_current: number;
    streak_longest: number;
    streak_active_today: boolean;
    sessions_total: number;
    sessions_by_difficulty: SessionsByDifficulty;
    distinct_scenarios: number;
    badges: BadgeResponse[];
    recent_xp: RecentXpEntry[];
    last_active: string | null;
}

export const fetchMyGamification = async (token: string): Promise<GamificationMeResponse> => {
    const res = await fetch(`${getBackendUrl()}/gamification/me`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch gamification data');
    return res.json();
};

// Same shape as /me, for an admin/trainer viewing another user's gamification
// stats on the user detail page. Authorization is enforced server-side (self for
// trainers, same-org for admins, any user for super_admins).
export const fetchUserGamification = async (token: string, userId: string): Promise<GamificationMeResponse> => {
    const res = await fetch(`${getBackendUrl()}/gamification/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch user gamification data');
    return res.json();
};

export const markBadgesSeen = async (token: string, badgeKeys: BadgeKey[]): Promise<void> => {
    if (badgeKeys.length === 0) return;
    const res = await fetch(`${getBackendUrl()}/gamification/badges/mark-seen`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ badge_keys: badgeKeys }),
    });
    if (!res.ok) throw new Error('Failed to mark badges as seen');
};

// ─── /gamification/leaderboard ───────────────────────────────────────────────

export interface LeaderboardEntry {
    rank: number;
    user_id: string;
    display_name: string;
    email: string | null;
    level: number;
    level_name: string;
    xp_in_period: number;
    xp_total: number;
    streak_current: number;
    badge_count: number;
    sessions_in_period: number;
    is_self: boolean;
}

export interface LeaderboardResponse {
    period: LeaderboardPeriod;
    entries: LeaderboardEntry[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
}

export type LeaderboardSortColumn = 'rank' | 'name' | 'email' | 'level' | 'xp' | 'streak' | 'sessions';
export type LeaderboardSortOrder = 'asc' | 'desc';

export const fetchLeaderboard = async (
    token: string,
    period: LeaderboardPeriod = 'all_time',
    page = 1,
    pageSize = 25,
    sortBy?: LeaderboardSortColumn,
    sortOrder: LeaderboardSortOrder = 'asc',
): Promise<LeaderboardResponse> => {
    const params = new URLSearchParams({ period, page: String(page), page_size: String(pageSize) });
    if (sortBy) {
        params.set('sort_by', sortBy);
        params.set('sort_order', sortOrder);
    }
    const url = `${getBackendUrl()}/gamification/leaderboard?${params.toString()}`;
    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch leaderboard');
    return res.json();
};

// ─── /gamification/activity ──────────────────────────────────────────────────

export interface ActivityDay {
    date: string;
    count: number;
}

export interface ActivityResponse {
    days: ActivityDay[];
    total_sessions: number;
    total_active_days: number;
    max_streak: number;
}

const _DEFAULT_TZ = 'America/Chicago';

const getBrowserTz = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || _DEFAULT_TZ;
    } catch {
        return _DEFAULT_TZ;
    }
};

export const fetchActivity = async (token: string): Promise<ActivityResponse> => {
    const tz = encodeURIComponent(getBrowserTz());
    const res = await fetch(`${getBackendUrl()}/gamification/activity?tz=${tz}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch activity data');
    return res.json();
};

// ─── /gamification/roster ────────────────────────────────────────────────────

export interface RosterEntry {
    rank: number;
    user_id: string;
    display_name: string;
    email: string | null;
    phone: string | null;
    user_from: string | null;
    level: number;
    level_name: string;
    xp_in_period: number;
    xp_total: number;
    streak_current: number;
    badge_count: number;
    sessions_in_period: number;
    sessions_total: number;
    last_active: string | null;
}

export interface RosterSummary {
    total_users: number;
    avg_xp: number;
    top_streak: number;
    active_this_week: number;
    total_sessions_in_period: number;
    re_engagement_names: string[];
}

export interface TrainerRosterResponse {
    period: LeaderboardPeriod;
    organization_id: string;
    summary: RosterSummary;
    entries: RosterEntry[];
}

export const fetchTrainerRoster = async (
    token: string,
    period: LeaderboardPeriod = 'all_time',
    organizationId?: string,
): Promise<TrainerRosterResponse> => {
    const params = new URLSearchParams({ period });
    if (organizationId) params.set('organization_id', organizationId);
    const res = await fetch(`${getBackendUrl()}/gamification/roster?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch trainer roster');
    return res.json();
};

// ─── /gamification/platform/* (super-admin only) ─────────────────────────────

export interface PlatformSummaryResponse {
    total_organizations: number;
    total_users: number;
    total_sessions: number;
    platform_xp: number;
}

export interface OrgPerformanceEntry {
    organization_id: string;
    organization_name: string;
    user_count: number;
    total_xp: number;
    sessions: number;
}

export interface OrgPerformanceResponse {
    organizations: OrgPerformanceEntry[];
    total: number;
    page: number;
    page_size: number;
}

export interface PlatformUserEntry {
    rank: number;
    user_id: string;
    display_name: string;
    organization_id: string;
    organization_name: string;
    role: string;
    level: number;
    level_name: string;
    xp_in_period: number;
    streak_current: number;
    badge_count: number;
    sessions_in_period: number;
    last_active: string | null;
}

export interface PlatformUserLeaderboardResponse {
    period: LeaderboardPeriod;
    entries: PlatformUserEntry[];
    total: number;
    page: number;
    page_size: number;
}

export const fetchPlatformSummary = async (token: string): Promise<PlatformSummaryResponse> => {
    const res = await fetch(`${getBackendUrl()}/gamification/platform/summary`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch platform summary');
    return res.json();
};

/**
 * Organization columns the backend can order across the whole platform.
 *
 * Only these two: both come straight from Postgres. "No. of Users" and "Total XP" are
 * assembled per-org from Keycloak for the fetched page only (see platform_service.py),
 * so ranking every org by either would cost one Keycloak call per org — the table
 * sorts those two within the page on screen instead, and labels them as such.
 */
export type OrgPerformanceSortColumn = 'name' | 'sessions';

export interface OrgPerformanceFilters {
    /** Bounds on total sessions — the one org metric Postgres can filter on its own. */
    sessions_min?: number;
    sessions_max?: number;
}

export const fetchOrgPerformance = async (
    token: string,
    search?: string,
    page = 1,
    pageSize = 25,
    sortBy?: OrgPerformanceSortColumn,
    sortOrder: 'asc' | 'desc' = 'asc',
    filters: OrgPerformanceFilters = {},
): Promise<OrgPerformanceResponse> => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (search) params.set('search', search);
    if (sortBy) {
        params.set('sort_by', sortBy);
        params.set('sort_order', sortOrder);
    }
    // Explicit undefined check, not a truthiness one: `sessions_min=0` is a meaningful
    // bound (orgs with no sessions at all) and would be dropped by `if (value)`.
    if (filters.sessions_min !== undefined) params.set('sessions_min', String(filters.sessions_min));
    if (filters.sessions_max !== undefined) params.set('sessions_max', String(filters.sessions_max));
    const res = await fetch(`${getBackendUrl()}/gamification/platform/organizations?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch organization performance');
    return res.json();
};

export interface PlatformUserFilters {
    organization?: string;
    organization_id?: string;
    /** One query param per role (`role=trainer&role=trainee`) — the backend matches any of them. */
    role?: string[];
    level_name?: string;
    xp_min?: number;
    xp_max?: number;
    streak_min?: number;
    streak_max?: number;
    sessions_min?: number;
    sessions_max?: number;
}

function appendPlatformUserFilterParams(params: URLSearchParams, filters?: PlatformUserFilters): void {
    if (!filters) return;
    Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v));
        } else {
            params.set(key, String(value));
        }
    });
}

export type PlatformUserSortColumn = 'name' | 'xp' | 'streak' | 'badges' | 'sessions';

export const fetchPlatformUsers = async (
    token: string,
    period: LeaderboardPeriod = 'all_time',
    page = 1,
    pageSize = 25,
    filters?: PlatformUserFilters,
    sortBy?: PlatformUserSortColumn,
    sortOrder: 'asc' | 'desc' = 'asc',
): Promise<PlatformUserLeaderboardResponse> => {
    const params = new URLSearchParams({ period, page: String(page), page_size: String(pageSize) });
    appendPlatformUserFilterParams(params, filters);
    if (sortBy) {
        params.set('sort_by', sortBy);
        params.set('sort_order', sortOrder);
    }
    const res = await fetch(`${getBackendUrl()}/gamification/platform/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch platform user leaderboard');
    return res.json();
};

export const downloadPlatformUsersCsv = async (
    token: string,
    period: LeaderboardPeriod = 'all_time',
    filters?: PlatformUserFilters,
): Promise<void> => {
    const params = new URLSearchParams({ period });
    appendPlatformUserFilterParams(params, filters);
    appendCsvLanguage(params);
    const res = await fetch(`${getBackendUrl()}/gamification/platform/users/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to export user leaderboard');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = csvFileName('userLeaderboard', 'user_leaderboard');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};
